import useSWR from 'swr'
import useAuth from '@crystal-eyes/hooks/useAuth'
import { fetchV3ApiAuthed } from '@crystal-eyes/utils/apis/v3Api'
import useProfileId from '@crystal-eyes/hooks/profiles/useProfileId'
import { useCallback } from 'react'

interface Identity {
  id: string
}

type State = {
  data?: { id: string; name: string }[]
  error?: any
  loading: boolean
  addTag: (tag: { name: string }) => Promise<void>
  removeTag: (tag: { id: string }) => Promise<void>
}

export default function useProfileTags({
  identity,
}: {
  identity?: Identity
}): State {
  const { data: auth } = useAuth()
  const { data: profileId } = useProfileId({ identity: identity! })

  const fetcher = ([__key, profileId]: [string, string]) =>
    fetchV3ApiAuthed(auth, `profiles/${profileId}/groupings`, {
      method: 'GET',
    }).then(async res => (await res.json()).data)

  const {
    data,
    isLoading,
    error,
    mutate: mutateTags,
  } = useSWR(
    auth?.authed && profileId ? ['useProfileTags', profileId] : null,
    fetcher,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    },
  )

  const addTag = useCallback(
    async (tag: { name: string }) => {
      const newTags = [...(data || []), tag]
      mutateTags(newTags, { revalidate: false })

      return fetchV3ApiAuthed(auth, `profiles/${profileId}/groupings`, {
        method: 'POST',
        body: JSON.stringify({ tag_name: tag.name }),
      }).then(() => {
        mutateTags(newTags, { revalidate: true })
      })
    },
    [profileId, data, auth, mutateTags],
  )

  const removeTag = useCallback(
    async (tag: { id: string }) => {
      const newTags = (data || []).filter(
        (currTag: { id: string }) => currTag.id !== tag.id,
      )
      mutateTags(newTags, { revalidate: false })

      return fetchV3ApiAuthed(
        auth,
        `profiles/${profileId}/groupings/${tag.id}`,
        {
          method: 'DELETE',
        },
      ).then(() => {
        mutateTags(newTags, { revalidate: true })
      })
    },
    [profileId, data, auth, mutateTags],
  )

  return {
    data,
    loading: isLoading,
    error,
    addTag,
    removeTag,
  }
}
